import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Palette, Video, Building2, 
  Users, Camera, Megaphone, GraduationCap,
  CheckCircle, Upload, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';

const iconMap = { Palette, Video, Building2, Megaphone, GraduationCap, Camera };

const CASES = [
  {
    icon: 'Palette',
    title: 'Créatifs & Designers',
    description: 'Envoyez vos maquettes, fichiers sources PSD/AI/Figma et exports haute résolution à vos clients sans compression ni perte de qualité.',
    benefits: ['Fichiers PSD, AI, Sketch jusqu\'à 20 Go', 'Aperçu des images pour vos clients', 'Fond personnalisé avec votre branding', 'Lien de téléchargement professionnel'],
    testimonial: { quote: 'SENDBOX a changé ma façon de livrer mes projets. Mes clients adorent la simplicité.', author: 'Marie D.', role: 'Graphiste freelance' }
  },
  {
    icon: 'Video',
    title: 'Vidéastes & Photographes',
    description: 'Partagez vos rushes, exports 4K et RAW en toute simplicité.',
    benefits: ['Vidéos 4K/8K sans compression', 'Fichiers RAW et ProRes', 'Transfert sécurisé pour vos clients', 'Notification quand le client télécharge'],
    testimonial: { quote: 'Enfin une solution qui gère mes fichiers vidéo de 15 Go sans broncher.', author: 'Thomas L.', role: 'Réalisateur' }
  },
  {
    icon: 'Megaphone',
    title: 'Marketing & Communication',
    description: 'Diffusez vos campagnes, assets de marque et kits presse à vos partenaires et médias en quelques clics.',
    benefits: ['Kits presse complets', 'Assets de marque organisés', 'Liens partageables par email', 'Statistiques de téléchargement'],
    testimonial: { quote: 'On envoie nos kits presse en 30 secondes. Le QR code est un game changer.', author: 'Sophie M.', role: 'Responsable RP' }
  },
  {
    icon: 'Building2',
    title: 'Entreprises & Teams',
    description: 'Sécurisez vos échanges de documents confidentiels avec le chiffrement de bout en bout et la protection par mot de passe.',
    benefits: ['Chiffrement E2E des documents', 'Protection par mot de passe', 'Expiration automatique des liens', 'Conformité RGPD'],
    testimonial: { quote: 'La sécurité de SENDBOX nous permet d\'envoyer des documents sensibles en toute confiance.', author: 'Jean-Pierre R.', role: 'DSI' }
  },
  {
    icon: 'GraduationCap',
    title: 'Éducation & Formation',
    description: 'Partagez vos cours, présentations et ressources pédagogiques avec vos étudiants ou apprenants.',
    benefits: ['Supports de cours volumineux', 'Vidéos de formation', 'Partage facile par QR code', 'Gratuit pour l\'essentiel'],
    testimonial: { quote: 'Mes étudiants récupèrent les cours en scannant un QR code. Simple et efficace.', author: 'Claire B.', role: 'Professeure' }
  },
  {
    icon: 'Camera',
    title: 'Événements & Mariages',
    description: 'Partagez les photos et vidéos de vos événements avec tous les participants grâce à un simple lien ou QR code.',
    benefits: ['Galeries photos complètes', 'Vidéos de la journée', 'QR code à afficher sur place', 'Téléchargement pour tous les invités'],
    testimonial: { quote: 'Tous nos invités ont pu récupérer les photos du mariage grâce au QR code. Magique !', author: 'Émilie & Paul', role: 'Mariés 2025' }
  }
];

const UseCasesPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <SEOHead 
        title="Cas d'usage" 
        description="Découvrez comment des milliers de professionnels utilisent SENDBOX pour simplifier leurs échanges de fichiers au quotidien." 
      />
      <NavigationMenu />
      
      <main className="pt-24 pb-32">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm mb-6">
            <Users className="w-4 h-4" />
            Cas d'usage
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="usecases-title">
            SENDBOX s'adapte<br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              à votre métier
            </span>
          </h1>
          <p className="text-lg md:text-xl text-blue-200/70 max-w-3xl mx-auto">
            Découvrez comment des milliers de professionnels utilisent SENDBOX pour simplifier 
            leurs échanges de fichiers au quotidien.
          </p>
        </section>

        {/* Use Cases */}
        <section className="max-w-6xl mx-auto px-6">
          <div className="space-y-40">
            {CASES.map((useCase, index) => {
              const Icon = iconMap[useCase.icon] || Users;
              const isEven = index % 2 === 0;
              return (
                <div key={index}>
                  {index > 0 && (
                    <div className="flex justify-center mb-16">
                      <div className="w-24 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
                    </div>
                  )}
                <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`} data-testid={`usecase-${index}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-blue-500/20 rounded-xl">
                        <Icon className="w-6 h-6 text-blue-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-white">{useCase.title}</h2>
                    </div>
                    <p className="text-blue-200/70 text-lg mb-6">{useCase.description}</p>
                    <ul className="space-y-3 mb-6">
                      {useCase.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <span className="text-blue-100/80">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {useCase.testimonial && (
                    <div className="flex-1 max-w-md">
                      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6">
                        <div className="text-cyan-400 mb-4">
                          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                          </svg>
                        </div>
                        <p className="text-white text-lg mb-4 italic">"{useCase.testimonial.quote}"</p>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                            {useCase.testimonial.author?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-white font-medium">{useCase.testimonial.author}</p>
                            <p className="text-blue-300/60 text-sm">{useCase.testimonial.role}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* France/Europe Storage Banner */}
        <section className="max-w-6xl mx-auto px-6 mt-20 mb-8">
          <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/20 rounded-2xl p-8 flex items-center gap-6">
            <div className="flex-shrink-0 w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1" data-testid="usecases-france-storage">Vos fichiers sont stockes en France et en Europe</h3>
              <p className="text-blue-200/70 text-sm leading-relaxed">
                Tous les fichiers transférés via SENDBOX sont hébergés sur des serveurs situés en France et en Europe, garantissant une conformité totale avec le RGPD et la souveraineté numérique de vos données.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-6xl mx-auto px-6 mt-20 mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '500K+', label: 'Fichiers partagés' },
              { value: '50K+', label: 'Utilisateurs actifs' },
              { value: '99.9%', label: 'Disponibilité' },
              { value: '4.9/5', label: 'Satisfaction client' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-6 bg-slate-800/30 border border-blue-500/10 rounded-2xl">
                <p className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 mb-2">{stat.value}</p>
                <p className="text-blue-300/60 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-cyan-500/30 rounded-3xl p-10">
            <h2 className="text-3xl font-bold text-white mb-4">Rejoignez des milliers de professionnels</h2>
            <p className="text-blue-200/70 mb-8 max-w-xl mx-auto">Commencez à utiliser SENDBOX gratuitement dès maintenant.</p>
            <Link to="/">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                <Upload className="w-5 h-5 mr-2" />
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

export default UseCasesPage;
